/*
  SEARCH: WOMEN | MEN SWITCH (2026-09-25, MASTER_LIST C-21 steps 1–2). Markup and CSS: snippets/r-search-gender.liquid,
  rendered above the field in sections/search-drawer.liquid.
  Rishi: "Are we able to create a button that people can select to search for mens or womens inside the search box?"
  then "1. A  2. Women/Men  3. Shoppers last choice otherwise nothing."
  v3 (2026-09-26, C-22): "preselect women in all searches that have no gender and let the user switch manually." So
  "nothing" is gone: the switch always has Women or Men selected, Women when nothing else decides.

  WHAT IS SELECTED WHEN THE DRAWER OPENS
    1. On /search: whatever the URL is filtered to (filter.p.product_type), so the switch matches the results on screen
       (r-search-route.js gives every search one). Re-read every time the drawer opens, because filters can change on
       that page without a reload.
    2. Elsewhere: the page's own gender (data-page-gender: a women's/men's collection or product).
    3. Otherwise the shopper's last choice this visit (sessionStorage), else WOMEN (v3, Rishi 2026-09-26).
    Tapping the selected option leaves it selected (v1–v2 cleared it, to reach "nothing").
    The last choice is saved when the shopper taps the switch and when they search; both count as choosing.

  WHAT A SEARCH SENDS WHILE WOMEN OR MEN IS SELECTED
    type=product & filter.p.product_type=<Womens|Mens>, as hidden fields in the form. The results page then shows the
    same choice as its ticked Men's/Women's filter, which the shopper can clear like any other filter.
    ⚠️ NOT "+ Unisex" (v2, 2026-09-25). v1 also sent filter.p.product_type=Unisex. When the shopper removed the
    "Womens" chip, the Unisex value stayed on as an invisible filter (Search & Discovery hides a value with 0 matches),
    so the page went blank with nothing to clear. ⚠️ Since v3 every search has Women or Men, so the 18 unisex products
    store-wide show in search only if the shopper removes the gender filter on the results page.
    ⚠️ type=product is REQUIRED, not tidiness. Without a type the results page searches products, articles and pages,
    and then loads each tab by Ajax from a URL that carries only q (main-search.liquid, the `load-from-url` panels), so the
    gender filter would be silently dropped.
    ⚠️ A GENDER WORD IN THE QUERY WINS ('mens loafers' with WOMEN selected): no hidden fields are added, and
    r-search-route.js gives the search the typed gender's filter before the results page is drawn.
    ⚠️ ONLY REAL SEARCH BOXES (v2): a GET form to /search whose `q` is a visible <input type="search">. v1 accepted any
    form with a field named q, and the results page's Filter & sort form (#facets-drawer-form) carries a HIDDEN q. The
    gender fields then rode along with every filter and sort: the Men's/Women's box could not be unticked, tapping the
    switch changed the next sort's results, and the URL got duplicate values (found by the 2026-09-25 verification).
    ⚠️ The fields are kept in sync on every keystroke and switch change, not added at submit time, so anything that
    reads the form at submit (the theme, or a script listening in the capture phase) already sees the right fields.

  THE DROPDOWN (predictive search)
    theme.js fetches /search/suggest?q=…&section_id=predictive-search and, through its private `cachedFetch`, keeps every
    answer in memory keyed by that URL. The URL never mentions gender, so a cached answer would survive a switch change
    and show the wrong gender. So window.fetch is wrapped for predictive-search URLs only:
      - EVERY predictive answer is returned with a content type that cachedFetch doesn't memoise (it only keeps text/* and
        JSON). This file keeps its own small memory instead, keyed by gender + URL (v2), so retyping a query or switching
        back and forth does not go back to the network. That matters because the scoped products come from the /search
        route, which rate-limits (HTTP 429) after about 10 fast requests.
      - The gender is the one Enter would open (v3.1): the router's gender for a designer/category search, else a typed
        gender word, else the switch. Both genders typed: unscoped.
      - With Women or Men selected, /search/suggest still supplies the query suggestions and collections. The products
        come from /search?type=product&section_id=predictive-search plus the gender filter; the section renders
        `search.results` when `predictive_search` isn't set.
        The product query is the typed words as a prefix search (v3: the retired exact-match rewrite is gone).
        ⚠️ NO LOOSE FALLBACK WHEN THE SCOPED QUERY FINDS NOTHING (v2): within a gender, 0 usually means "none of this
        for men/women", and an unscoped fallback filled the dropdown with unrelated products. The dropdown then shows
        no products; Enter still searches, and r-search-route.js may open the matching collection page.
      - Collections of the other gender (…mens-… / …womens-…) are dropped. Query suggestions get the scope added to
        their links, unless the suggestion names a gender itself ('balmain women'), which wins, as a typed word does.
      - If the scoped products request fails (a 429, a network error), the unscoped answer is shown unchanged, as it was
        before the switch existed, instead of an empty dropdown (v2).
    Changing the switch while results are showing re-runs the dropdown by replaying an `input` event on the field.

  REVERSIBLE: remove the render in sections/search-drawer.liquid (the snippet loads this file), or run
  nav-rebuild/search_gender_theme.py --revert <log> --apply.
*/
(() => {
  const KEY = 'r-search-gender';
  const TYPE = { women: 'Womens', men: 'Mens' };
  const OTHER_HANDLE = { women: 'mens', men: 'womens' };
  const FIELDS = 'data-r-search-gender';
  // The same gender words r-search-route.js reads (its GENDER table), so both scripts agree on when a typed word wins.
  // Apostrophes are dropped first: men's → mens.
  const GENDER_WORD = /(?:^| )(?:men|mens|man|mans|male|males|homme|guys|women|womens|woman|womans|ladies|lady|female|females|femme)(?: |$)/;
  const norm = (s) => (s || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[’']/g, '')
    .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  const typedGender = (q) => GENDER_WORD.test(norm(q));
  // Which one: 'women' / 'men', or null for none or both.
  const MEN_WORD = /(?:^| )(?:men|mens|man|mans|male|males|homme|guys)(?: |$)/;
  const WOMEN_WORD = /(?:^| )(?:women|womens|woman|womans|ladies|lady|female|females|femme)(?: |$)/;
  const typedWhich = (q) => {
    const n = norm(q); const m = MEN_WORD.test(n); const w = WOMEN_WORD.test(n);
    return m && !w ? 'men' : w && !m ? 'women' : null;
  };
  const onSearchPage = /\/search\/?$/.test(location.pathname);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ⚠️ Storage can be missing or throw (private windows, blocked site data). The switch must still work without it.
  const store = {
    get() { try { return sessionStorage.getItem(KEY); } catch (e) { return null; } },
    set(v) { try { sessionStorage.setItem(KEY, v); } catch (e) { /* no memory this visit; the switch still works */ } },
  };

  let state = null; // 'women' | 'men' | null
  let switchEl = null;

  const readUrlScope = () => {
    const v = new URLSearchParams(location.search).getAll('filter.p.product_type');
    const w = v.includes('Womens'); const m = v.includes('Mens');
    return w && !m ? 'women' : m && !w ? 'men' : null;
  };

  // Always 'women' or 'men' (v3). A stored 'none' from v1–v2 counts as no choice.
  const initialState = (pageGender) => {
    if (onSearchPage) return readUrlScope() || 'women';
    const urlGender = readUrlScope(); // a page filtered to one gender (v3.1, verification 2026-09-26)
    if (urlGender) return urlGender;
    if (pageGender === 'women' || pageGender === 'men') return pageGender;
    const last = store.get();
    return last === 'women' || last === 'men' ? last : 'women';
  };

  // The form's visible search box, or null. A hidden `q` (the Filter & sort form's) does not count.
  const searchBox = (f) => {
    const q = f instanceof HTMLFormElement ? f.elements.namedItem('q') : null;
    return q instanceof HTMLInputElement && q.type === 'search' ? q : null;
  };
  const isSearchForm = (f) => {
    if (!searchBox(f) || (f.method || 'get').toLowerCase() !== 'get') return false;
    try { return /\/search\/?$/.test(new URL(f.action, location.href).pathname); } catch (e) { return false; }
  };
  const searchForms = () => [...document.querySelectorAll('form')].filter(isSearchForm);

  // ⚠️ The results page's OWN search box (main-search.liquid `.r-search-intro__form`) searches the gender the page is
  // filtered to, which its Women | Men links show. v3 gave it the drawer's state, so tapping Men in the drawer and closing
  // it silently turned that box into a men's search (verification 2026-09-26).
  const formState = (form) => (onSearchPage && form.matches('.r-search-intro__form') ? readUrlScope() : state);

  // Hidden fields for one form, from its state and that form's own query.
  const syncForm = (form) => {
    form.querySelectorAll(`input[${FIELDS}]`).forEach((i) => i.remove());
    const fs = formState(form);
    if (!fs || typedGender(searchBox(form).value)) return;
    // Both search boxes carry their own type=product since 2026-09-25 (the results page is products only); add one
    // only to a form without it, so the URL never gets two.
    const fields = [['filter.p.product_type', TYPE[fs]]];
    if (!form.querySelector(`input[name="type"]:not([${FIELDS}])`)) fields.unshift(['type', 'product']);
    for (const [name, value] of fields) {
      const i = document.createElement('input');
      i.type = 'hidden';
      i.name = name;
      i.value = value;
      i.setAttribute(FIELDS, '');
      form.appendChild(i);
    }
  };
  const syncAll = () => searchForms().forEach(syncForm);

  const renderSwitch = () => {
    if (!switchEl) return;
    switchEl.querySelectorAll('[data-gender]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gender === state)));
  };

  // Re-run the dropdown for the text already typed (theme.js listens for `input`, debounced).
  const refreshDropdown = () => {
    const input = document.getElementById('search-drawer-input');
    if (input && input.value) input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /* ---------------- the dropdown ---------------- */
  const isPredictive = (url) => /[?&]section_id=predictive-search(?:&|$)/.test(url) && /\/search(?:\/suggest)?\?/.test(url);
  const origFetch = window.fetch.bind(window);
  // ⚠️ Not text/* and not JSON, so theme.js's cachedFetch never memoises a predictive answer (see the header).
  const UNCACHED = { 'Content-Type': 'application/x-r-predictive-search; charset=utf-8' };
  const answer = (html, status = 200) => new Response(html, { status, headers: UNCACHED });

  // This file's own memory of successful answers, keyed by gender + URL. Bounded; oldest out first.
  const memory = new Map();
  const remember = (key, html) => {
    memory.set(key, html);
    if (memory.size > 60) memory.delete(memory.keys().next().value);
  };

  const scopedUrl = (query, g, prefix) => {
    const p = new URLSearchParams();
    p.set('q', query);
    p.set('type', 'product');
    if (prefix) p.set('options[prefix]', 'last');
    p.set('section_id', 'predictive-search');
    p.set('filter.p.product_type', TYPE[g]);
    return `${root}search?${p}`;
  };
  const okText = (label) => (r) => (r.ok ? r.text() : Promise.reject(new Error(`${label} ${r.status}`)));

  const withScope = (href, g) => {
    if (!g) return href;
    const u = new URL(href, location.origin);
    if (typedGender(u.searchParams.get('q') || '')) return href; // the suggestion names a gender itself: that wins
    u.searchParams.set('type', 'product');
    u.searchParams.set('filter.p.product_type', TYPE[g]);
    return u.pathname + u.search + u.hash;
  };

  // A suggestion's link, scoped to the gender a search for ITS words would open (the router's gender, else a typed gender
  // word in it, else the switch). Unscoped when its words name both genders.
  const rowScope = (href) => {
    let rq = '';
    try { rq = new URL(href, location.origin).searchParams.get('q') || ''; } catch (e) { return href; }
    return withScope(href, dropdownGender(rq));
  };

  // A tab button and its panel, found by what the panel holds. x-tabs pairs them by order.
  const tabPair = (tabs, selector) => {
    if (!tabs) return null;
    const kids = [...tabs.children];
    const buttons = kids.filter((k) => k.getAttribute('slot') === 'tab');
    const panels = kids.filter((k) => k.getAttribute('slot') === 'tabpanel');
    const i = panels.findIndex((p) => p.querySelector(selector));
    return i < 0 || !buttons[i] ? null : { button: buttons[i], panel: panels[i] };
  };

  const merge = (suggestHtml, productHtml, g, typed, sent) => {
    const parser = new DOMParser();
    const sDoc = parser.parseFromString(suggestHtml || '', 'text/html');
    const pDoc = parser.parseFromString(productHtml || '', 'text/html');
    const sRoot = sDoc.querySelector('.shopify-section');
    const pRoot = pDoc.querySelector('.shopify-section');
    if (!sRoot) return productHtml || suggestHtml;

    // ⚠️ EACH SUGGESTION IS SCOPED BY THE GENDER ITS OWN WORDS OPEN (v3.3, verification 2026-09-28), not the typed query's:
    // with WOMEN selected, 'versace jeans' routes to men's (no women's Versace jeans), and v3.2 then scoped every row to Mens,
    // so the 'versace jeans couture' row opened men's VJC.
    sRoot.querySelectorAll('.predictive-search__suggestions a[href]').forEach((a) => a.setAttribute('href', rowScope(a.getAttribute('href'))));

    let sTabs = sRoot.querySelector('x-tabs');
    const pTabs = pRoot && pRoot.querySelector('x-tabs');
    const scoped = tabPair(pTabs, '.predictive-search__products');
    if (!sTabs && scoped) {
      sRoot.querySelector('.predictive-search__no-results')?.remove();
      sTabs = sDoc.importNode(pTabs, true);
      sTabs.replaceChildren();
      sRoot.appendChild(sTabs);
    }
    if (sTabs) {
      const unscoped = tabPair(sTabs, '.predictive-search__products');
      if (unscoped) { unscoped.button.remove(); unscoped.panel.remove(); }
      if (scoped) sTabs.prepend(sDoc.importNode(scoped.button, true), sDoc.importNode(scoped.panel, true));

      const other = OTHER_HANDLE[g];
      const cols = tabPair(sTabs, '.predictive-search__collections');
      if (cols) {
        cols.panel.querySelectorAll('a[href*="/collections/"]').forEach((a) => {
          const handle = (new URL(a.getAttribute('href'), location.origin).pathname.split('/collections/')[1] || '').split('/')[0];
          if (handle === other || handle.startsWith(`${other}-`) || handle.includes(`-${other}-`) || handle.endsWith(`-${other}`)) a.remove();
        });
        if (!cols.panel.querySelector('a[href*="/collections/"]')) { cols.button.remove(); cols.panel.remove(); }
      }

      // The first remaining panel shows; the rest wait for their tab (the section's own `cycle` did this before the edit).
      const panels = [...sTabs.children].filter((k) => k.getAttribute('slot') === 'tabpanel');
      panels.forEach((p, i) => (i === 0 ? p.removeAttribute('hidden') : p.setAttribute('hidden', '')));
      if (!panels.length) sTabs.remove();
    }

    if (sRoot.querySelector('x-tabs [slot="tabpanel"], .predictive-search__suggestions li')) return sRoot.outerHTML;
    if (!pRoot) return sRoot.outerHTML;
    // Nothing for this gender. The scoped section's "no results" line quotes the query it was sent; make sure it shows
    // the shopper's own words.
    if (sent !== typed) {
      const walker = pDoc.createTreeWalker(pRoot, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) if (n.nodeValue.includes(sent)) n.nodeValue = n.nodeValue.split(sent).join(typed);
    }
    return pRoot.outerHTML;
  };

  // ⚠️ THE DROPDOWN SHOWS THE GENDER ENTER WILL OPEN (v3.1, verification 2026-09-26). With Women preselected, 'john
  // varvatos' filled the dropdown with other designers' women's products (Shopify's loose matching) and offered the empty
  // womens-john-varvatos, while Enter opened the men's page. r-search-route.js (loaded on every page) says which gender a
  // routed search lands on; a typed gender word scopes to that gender; otherwise the switch.
  const dropdownGender = (q) => {
    const R = window.rSearchRoute;
    const routed = R && R.genderFor ? R.genderFor(q, { women: 'w', men: 'm' }[state] || null) : null;
    if (routed) return routed === 'm' ? 'men' : 'women';
    if (typedGender(q)) return typedWhich(q); // null when both genders are typed: unscoped
    return state;
  };

  // ---- What the router knows, added to every dropdown answer (v3.2, 2026-09-28) ----
  // 1. Rishi's line for a designer we don't carry ('celine', 'canada goose jacket'), above everything else. The words
  //    live in Liquid: the drawer's <template id="r-search-not-carried"> (snippets/r-search-not-carried.liquid), the same
  //    snippet the results page uses.
  // 2. A family's other labels, as suggestions, when the shopper types only the family name (Rishi: "if there is a
  //    search suggestion for VJC when a customer types in Versace, that is enough … Same for moncler"). Shopify already
  //    suggests 'versace jeans couture' for 'versace', but nothing at all for 'moncler'; a row is added only when
  //    Shopify's own list doesn't have it. Same markup and link as Shopify's rows: the typed part in <mark>, the rest in
  //    <span>, a /search link the router then opens as the label's page, with the gender the dropdown is showing.
  const decorate = (html, q, g) => {
    const R = window.rSearchRoute;
    if (!R || !R.notCarried || !html) return html;
    const nc = R.notCarried(q);
    const fam = R.familyFor(q);
    if (!nc && !fam.length) return html;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sec = doc.querySelector('.shopify-section');
    if (!sec) return html;
    if (fam.length) {
      let list = sec.querySelector('.predictive-search__suggestions');
      if (!list) {
        list = doc.createElement('ul');
        list.className = 'predictive-search__suggestions unstyled-list scroll-area bleed';
        const next = [...sec.children].find((c) => c.matches('x-tabs, .predictive-search__no-results')) || null;
        sec.insertBefore(list, next);
      }
      const typed = norm(q);
      const have = new Set([...list.querySelectorAll('a')].map((x) => norm(x.textContent)));
      const first = list.firstElementChild;
      const at = first && norm(first.textContent) === typed ? first.nextElementSibling : list.firstElementChild;
      for (const m of fam) {
        if (have.has(m)) continue;
        const li = doc.createElement('li');
        const a = doc.createElement('a');
        a.className = 'link-reversed';
        const href = `${root}search?q=${encodeURIComponent(m).replace(/%20/g, '+')}&type=product`;
        a.setAttribute('href', rowScope(href));
        if (m.startsWith(`${typed} `)) {
          const mk = doc.createElement('mark'); mk.textContent = typed;
          const sp = doc.createElement('span'); sp.textContent = m.slice(typed.length);
          a.append(mk, sp);
        } else {
          a.textContent = m;
        }
        li.appendChild(a);
        list.insertBefore(li, at);
      }
    }
    if (nc) {
      const tpl = document.getElementById('r-search-not-carried');
      if (tpl) sec.prepend(doc.importNode(tpl.content, true));
    }
    return sec.outerHTML;
  };

  const predictive = async (url, init) => {
    const q = new URL(url, location.href).searchParams.get('q') || '';
    const g = dropdownGender(q);
    const key = `${g || 'none'}|${url}`;
    if (memory.has(key)) return answer(memory.get(key));

    if (!g) {
      const r = await origFetch(url, init);
      const html = decorate(await r.text(), q, g);
      if (r.ok) remember(key, html);
      return answer(html, r.status);
    }

    const sent = q;
    const [suggest, products] = await Promise.allSettled([
      origFetch(url, init).then(okText('suggest')),
      origFetch(scopedUrl(sent, g, true), init).then(okText('scoped')),
    ]);
    // The theme aborts the previous request on every keystroke; hand the abort back so it is ignored, as before.
    if (init && init.signal && init.signal.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
    if (suggest.status === 'rejected' && products.status === 'rejected') throw suggest.reason;
    if (products.status === 'rejected') return answer(decorate(suggest.value, q, g)); // degrade to the unscoped answer
    if (suggest.status === 'rejected') return answer(decorate(merge('', products.value, g, q, sent), q, g));
    const html = decorate(merge(suggest.value, products.value, g, q, sent), q, g);
    remember(key, html);
    return answer(html);
  };

  window.fetch = function rSearchGenderFetch(input, init) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
    if (!url || !isPredictive(url)) return origFetch(input, init);
    return predictive(url, init);
  };

  /* ---------------- the switch ---------------- */
  const start = () => {
    switchEl = document.querySelector('.r-search-gender');
    if (!switchEl) return;
    state = initialState(switchEl.dataset.pageGender || '');
    renderSwitch();
    switchEl.hidden = false;
    syncAll();

    // ⚠️ Keep the keyboard up: a mouse press on a switch button would otherwise take focus from the field.
    switchEl.addEventListener('mousedown', (e) => {
      if (e.target.closest('[data-gender]') && document.activeElement && document.activeElement.id === 'search-drawer-input') e.preventDefault();
    });
    switchEl.addEventListener('click', (e) => {
      const b = e.target.closest('[data-gender]');
      if (!b) return;
      state = b.dataset.gender; // v3: a two-way switch, one side always selected
      store.set(state);
      renderSwitch();
      syncAll();
      refreshDropdown();
    });

    // Any typing can add or remove a gender word, so re-derive the hidden fields for that form.
    document.addEventListener('input', (e) => {
      const f = e.target && e.target.form;
      if (f && isSearchForm(f)) syncForm(f);
    }, true);

    // A search counts as a choice: remember what it was scoped to, and make sure its fields are current.
    document.addEventListener('submit', (e) => {
      if (!isSearchForm(e.target)) return;
      syncForm(e.target);
      const typed = typedWhich(searchBox(e.target).value); // a typed gender word is the choice this search made
      if (typed || state) store.set(typed || state);
    }, true);

    // On /search the filter can be changed in place (facets update the URL without a reload). Follow the URL whenever
    // the shopper goes to search again: when the drawer is opened (v2; v1 waited for the field's focus, so the old
    // state showed for ~0.5 s as the drawer slid in) and when a search box takes focus.
    // ⚠️ ONLY WHEN THE URL'S FILTER HAS CHANGED since it was last read. Comparing against the current state instead
    // would undo a choice the shopper just tapped (tap Women, click into the field: focusin would reset it).
    if (onSearchPage) {
      let urlSeen = readUrlScope();
      const follow = () => {
        const now = readUrlScope();
        if (now !== urlSeen) { urlSeen = now; state = now || 'women'; renderSwitch(); syncAll(); }
      };
      document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest('[aria-controls="search-drawer"]')) follow(); }, true);
      document.addEventListener('focusin', (e) => { if (e.target && e.target.form && isSearchForm(e.target.form)) follow(); });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
